<script setup lang="ts">
import { watch } from 'vue'
import { useAuthStore } from '@/stores/auth'
import { supabase } from '@/supabase'
import { RouterView, useRouter, useRoute } from 'vue-router'
import type { RouteLocationRaw } from 'vue-router'
import AppShell from '@/components/layout/AppShell.vue'
import { isLocalDevAuthBypass } from '@/config/runtimeEnv'
import { deferAuthWork } from '@/utils/navigationRecovery'

const router = useRouter()
const route = useRoute()
const authStore = useAuthStore()

if (!isLocalDevAuthBypass()) {
    supabase.auth.onAuthStateChange((event) => {
        deferAuthWork(() => {
            if (event === 'SIGNED_IN' || event === 'INITIAL_SESSION') {
                authStore.loadUser().then(() => {
                    if (event === 'SIGNED_IN') authStore.loadRedirectRoute()
                })
            }
            else if (event === 'SIGNED_OUT') {
                authStore.clearUser()
            }
        })
    })
}

authStore.$onAction(({ name, store, after }) => {
    if (name === 'loadRedirectRoute') {
        after(async () => {
            const redirectRoute = store.redirectRoute

            if (redirectRoute) {
                await router.isReady()
                await router.replace(redirectRoute as RouteLocationRaw)
                authStore.clearRedirectRoute()
            }
        })
    }
})

watch(
    () => authStore.isAuthenticated,
    async (authed, wasAuthed) => {
        if (isLocalDevAuthBypass()) return
        if (authed && !wasAuthed && route.path === '/login') {
            await router.isReady()
            router.replace('/')
        }
        else if (!authed && wasAuthed) {
            router.replace('/login')
        }
    },
)

const showShell = () => route.path !== '/login' && authStore.isAuthenticated
</script>

<template>
    <AppShell v-if="showShell()">
        <RouterView />
    </AppShell>
    <RouterView v-else />
</template>
